/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import merge from "lodash/fp/merge";
import { matchPath } from "react-router-dom";
import { DeepPartial } from "redux";

export interface FallbackLoader {
  enabled: boolean;
  style: "dark" | "light";
  size: "full" | "content";
}

export interface RouteConfig {
  auth: boolean;
  allowedWithToken: string | null;
  header: boolean;
  pageHeader: {
    enabled: boolean;
    title: string;
  };
  reportTracker: boolean;
  routeFallbackLoader: FallbackLoader;
  warnUnsavedChanges: boolean;
  twoFactorAuth: boolean;
  canBypassTwoFactorAuth: boolean;
}

const defaultRouteConfig: RouteConfig = {
  auth: true,
  allowedWithToken: null,
  header: true,
  pageHeader: {
    enabled: true,
    title: "",
  },
  reportTracker: true,
  routeFallbackLoader: {
    enabled: true,
    style: "light",
    size: "content",
  },
  warnUnsavedChanges: false,
  twoFactorAuth: false,
  canBypassTwoFactorAuth: false,
};

const boxedGuestPageDefaultConfig: DeepPartial<RouteConfig> = {
  auth: false,
  header: false,
  pageHeader: {
    enabled: false,
  },
  reportTracker: false,
  routeFallbackLoader: {
    enabled: true,
    style: "dark",
    size: "full",
  },
  twoFactorAuth: false,
};

const routes: Record<string, DeepPartial<RouteConfig>> = {
  "/": {
    auth: false,
    pageHeader: {
      enabled: false,
    },
    reportTracker: false,
    routeFallbackLoader: {
      style: "dark",
      size: "full",
    },
  },
  "/app": boxedGuestPageDefaultConfig,
  "/app/learn-more/affiliates": {
    auth: false,
    pageHeader: {
      title: "page_header_title.learn_more",
    },
  },
  "/app/learn-more/customizations": {
    auth: false,
    pageHeader: {
      title: "page_header_title.learn_more",
    },
  },
  "/app/learn-more/network": {
    auth: false,
    pageHeader: {
      title: "page_header_title.learn_more",
    },
  },
  "/app/learn-more/product": {
    auth: false,
    pageHeader: {
      title: "page_header_title.learn_more",
    },
  },
  "/app/learn-more/webcams": {
    auth: false,
    pageHeader: {
      title: "page_header_title.learn_more",
    },
  },
  "/app/my-account/documents": {
    pageHeader: {
      title: "page_header_title.my_account",
    },
    twoFactorAuth: true,
    canBypassTwoFactorAuth: true,
    warnUnsavedChanges: true,
  },
  "/app/my-account/my-details": {
    pageHeader: {
      title: "page_header_title.my_account",
    },
    twoFactorAuth: true,
    warnUnsavedChanges: true,
  },
  "/app/my-account/email-preferences": {
    allowedWithToken: "tokenv2",
    pageHeader: {
      title: "page_header_title.email_preferences",
    },
    routeFallbackLoader: {
      size: "full",
    },
  },
  "/app/my-account/payout-info": {
    pageHeader: {
      title: "page_header_title.my_account",
    },
    twoFactorAuth: true,
    warnUnsavedChanges: true,
  },
  "/app/signup/resendverify": boxedGuestPageDefaultConfig,
  "/app/signup/verify": boxedGuestPageDefaultConfig,
  "/app/support/api": {
    pageHeader: {
      title: "page_header_title.support",
    },
  },
  "/app/support/contact": {
    auth: false,
    pageHeader: {
      title: "page_header_title.support",
    },
  },
  "/app/support/faq": {
    auth: false,
    pageHeader: {
      title: "page_header_title.support",
    },
  },
  "/app/compliance": {
    auth: false,
    pageHeader: {
      title: "page_header_title.compliance",
    },
  },
  "/app/dashboard": {
    pageHeader: {
      title: "page_header_title.dashboard",
    },
  },
  "/app/forgot-password": boxedGuestPageDefaultConfig,
  "/app/login": boxedGuestPageDefaultConfig,
  "/app/forced-password-reset": boxedGuestPageDefaultConfig,
  "/app/promo-tools": {
    pageHeader: {
      title: "page_header_title.promo_tools",
    },
  },
  "/app/reset-password": boxedGuestPageDefaultConfig,
  "/app/twofactorauth": boxedGuestPageDefaultConfig,
  "/app/signup": boxedGuestPageDefaultConfig,
  "/app/setpassword": boxedGuestPageDefaultConfig,
  "/app/tos": {
    auth: false,
    pageHeader: {
      title: "page_header_title.tos",
    },
  },
  "/app/privacy-policy": {
    auth: false,
    pageHeader: {
      title: "page_header_title.privacy_policy",
    },
  },
  "/app/my-sites/manage-sites": {
    pageHeader: {
      title: "page_header_title.my-sites",
    },
  },
  "/app/my-sites/create-new-site": {
    pageHeader: {
      title: "page_header_title.my-sites",
    },
  },
  "/app/my-sites/create-new-site/white-label": {
    pageHeader: {
      title: "page_header_title.my-sites",
    },
  },
  "/app/my-sites/create-new-site/promote-site": {
    pageHeader: {
      title: "page_header_title.my-sites",
    },
  },
  "/app/my-sites/create-new-site/black-label": {
    pageHeader: {
      title: "page_header_title.my-sites",
    },
  },
  "/app/my-sites/postbacks": {
    pageHeader: {
      title: "page_header_title.my-sites",
    },
  },
  "/app/statistics": {
    pageHeader: {
      title: "page_header_title.statistics",
    },
  },
};

export const getRouteConfig = (pathName: string) => {
  const routeNames = Object.keys(routes);
  const matchedRoute = routeNames.find((pattern) => matchPath(pattern, pathName));

  return merge(defaultRouteConfig, matchedRoute ? routes[matchedRoute] : {});
};
